import {useState} from "react";
import {ArrowRight, CalendarCheck, FolderOpen, GraduationCap, Zap} from "lucide-react";
import {useTranslationWithKeys} from "../../hooks/useTranslationWithKeys";
import {NotificationBanner} from "./banners/NotificationBanner";
import {AlumniDialog} from "../dialogs/user/AlumniDialog";

const FEATURES = [
    {icon: CalendarCheck, key: 'alumni.bannerFeatures.access'},
    {icon: FolderOpen, key: 'alumni.bannerFeatures.projects'},
    {icon: Zap, key: 'alumni.bannerFeatures.simulations'},
];

interface AlumniBannerProps {
    onClose: () => void;
}

/**
 * Alumni offer, rendered at `hero` prominence: it belongs in the
 * {@link HeroBannerSlot} rather than the floating banner stack.
 */
export function AlumniBanner({onClose}: AlumniBannerProps) {
    const {t} = useTranslationWithKeys();
    const [dialogOpen, setDialogOpen] = useState(false);

    return (
        <>
            <NotificationBanner
                variant="promo"
                prominence="hero"
                icon={<GraduationCap/>}
                decoration={
                    <GraduationCap
                        aria-hidden="true"
                        className="pointer-events-none absolute -top-8 -right-8 hidden h-56 w-56 rotate-12 opacity-10 lg:block"
                    />
                }
                message={t('alumni.bannerTitle')}
                description={t('alumni.bannerSubtitle')}
                aside={
                    <ul className="grid gap-3 sm:grid-cols-3 lg:mr-16 lg:grid-cols-1">
                        {FEATURES.map(({icon: Icon, key}) => (
                            <li
                                key={key}
                                className="flex items-center gap-3 rounded-lg bg-white/15 px-4 py-3 font-semibold backdrop-blur-sm lg:text-lg"
                            >
                                <Icon aria-hidden="true" className="h-5 w-5 flex-shrink-0 lg:h-6 lg:w-6"/>
                                {t(key)}
                            </li>
                        ))}
                    </ul>
                }
                action={{
                    label: (
                        <>
                            {t('alumni.activateButton')}
                            <ArrowRight className="h-5 w-5"/>
                        </>
                    ),
                    onClick: () => setDialogOpen(true),
                }}
                onClose={onClose}
            />
            <AlumniDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                onSuccess={onClose}
            />
        </>
    );
}
